// Contact import and campaigns (WA-07, WA-08). PRD §3, §6, §13, §17; A25.
import { z } from 'zod';
import * as C from './api-campaigns.js';
import { OkSchema } from './api.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

const Imp = W({ importId: IdParam });
const Camp = W({ campaignId: IdParam });
/** CSV body: streamed text/csv attachment; errors still use the JSON envelope. */
const Csv = z.unknown();

export const campaignRoutes = {
  // ─────────── Contact import (WA-07) ───────────
  listContactImports: route({
    method: 'GET', path: '/workspaces/:workspaceId/contact-imports', summary: 'Importaciones de contactos', tag: 'campaigns',
    minRole: 'manager', params: Ws, response: items(C.ContactImportSchema),
  }),
  uploadContactImport: route({
    method: 'POST', path: '/workspaces/:workspaceId/contact-imports', summary: 'Subir CSV de contactos (multipart/form-data, campo "file")', tag: 'campaigns',
    minRole: 'manager', params: Ws, body: C.UploadContactImportFieldsSchema, response: C.ContactImportSchema, status: 201,
  }),
  getContactImport: route({
    method: 'GET', path: '/workspaces/:workspaceId/contact-imports/:importId', summary: 'Importación de contactos', tag: 'campaigns',
    minRole: 'manager', params: Imp, response: C.ContactImportSchema,
  }),
  previewContactImport: route({
    method: 'POST', path: '/workspaces/:workspaceId/contact-imports/:importId/preview', summary: 'Guardar el mapeo y validar cada renglón (no escribe contactos)', tag: 'campaigns',
    minRole: 'manager', params: Imp, body: C.ContactImportMappingSchema, response: C.ContactImportPreviewSchema,
  }),
  listContactImportRows: route({
    method: 'GET', path: '/workspaces/:workspaceId/contact-imports/:importId/rows', summary: 'Renglones validados de la vista previa', tag: 'campaigns',
    minRole: 'manager', params: Imp, query: C.ContactImportRowsQuerySchema, response: C.ContactImportRowPageSchema,
  }),
  downloadContactImportErrors: route({
    method: 'GET', path: '/workspaces/:workspaceId/contact-imports/:importId/errors.csv', summary: 'CSV de renglones con errores, duplicados o en revisión', tag: 'campaigns',
    minRole: 'manager', params: Imp, response: Csv, produces: 'text/csv',
  }),
  applyContactImport: route({
    method: 'POST', path: '/workspaces/:workspaceId/contact-imports/:importId/apply', summary: 'Aplicar la importación (idempotente; nunca fusiona coincidencias ambiguas)', tag: 'campaigns',
    minRole: 'manager', params: Imp, body: C.ApplyContactImportSchema, response: C.ContactImportSchema,
  }),
  cancelContactImport: route({
    method: 'POST', path: '/workspaces/:workspaceId/contact-imports/:importId/cancel', summary: 'Descartar una importación no aplicada', tag: 'campaigns',
    minRole: 'manager', params: Imp, response: C.ContactImportSchema,
  }),

  // ─────────── Campaigns (WA-08) ───────────
  campaignCapabilities: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns/capabilities', summary: 'Estado de WhatsApp, plantillas aprobadas y límites para campañas', tag: 'campaigns',
    minRole: 'manager', params: Ws, response: C.CampaignCapabilitiesSchema,
  }),
  previewCampaignAudience: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/audience-preview', summary: 'Audiencia exacta con exclusiones explicadas y mensajes de ejemplo (no envía nada)', tag: 'campaigns',
    minRole: 'manager', params: Ws, body: C.AudiencePreviewRequestSchema, response: C.AudiencePreviewSchema,
  }),
  listCampaigns: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns', summary: 'Campañas', tag: 'campaigns',
    minRole: 'manager', params: Ws, response: items(C.CampaignSummarySchema),
  }),
  createCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns', summary: 'Crear campaña (borrador)', tag: 'campaigns',
    minRole: 'manager', params: Ws, body: C.CampaignInputSchema, response: C.CampaignSchema, status: 201,
  }),
  getCampaign: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns/:campaignId', summary: 'Campaña', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  updateCampaign: route({
    method: 'PATCH', path: '/workspaces/:workspaceId/campaigns/:campaignId', summary: 'Editar campaña (solo borradores)', tag: 'campaigns',
    minRole: 'manager', params: Camp, body: C.UpdateCampaignSchema, response: C.CampaignSchema,
  }),
  deleteCampaign: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/campaigns/:campaignId', summary: 'Eliminar borrador', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: OkSchema,
  }),
  getCampaignAudience: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns/:campaignId/audience', summary: 'Audiencia de la campaña (en vivo en borrador; instantánea una vez iniciada)', tag: 'campaigns',
    minRole: 'manager', params: Camp, query: C.AudienceQuerySchema, response: C.AudiencePreviewSchema,
  }),
  submitCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/submit', summary: 'Enviar a aprobación', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  approveCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/approve', summary: 'Aprobar (administrador distinto de quien la creó)', tag: 'campaigns',
    minRole: 'admin', params: Camp, response: C.CampaignSchema,
  }),
  rejectCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/reject', summary: 'Rechazar y regresar a borrador', tag: 'campaigns',
    minRole: 'admin', params: Camp, body: C.RejectCampaignSchema, response: C.CampaignSchema,
  }),
  returnCampaignToDraft: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/draft', summary: 'Regresar a borrador para editar (anula la aprobación)', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  startCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/start', summary: 'Iniciar o programar (fija la audiencia; cada envío se vuelve a revisar)', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  pauseCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/pause', summary: 'Pausar', tag: 'campaigns',
    minRole: 'manager', params: Camp, body: C.PauseCampaignSchema, response: C.CampaignSchema,
  }),
  resumeCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/resume', summary: 'Reanudar', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  cancelCampaign: route({
    method: 'POST', path: '/workspaces/:workspaceId/campaigns/:campaignId/cancel', summary: 'Cancelar (los envíos pendientes no salen)', tag: 'campaigns',
    minRole: 'manager', params: Camp, response: C.CampaignSchema,
  }),
  getCampaignReport: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns/:campaignId/report', summary: 'Reporte de entrega, respuestas y citas atribuidas', tag: 'campaigns',
    minRole: 'manager', params: Camp, query: C.CampaignReportQuerySchema, response: C.CampaignReportSchema,
  }),
  listCampaignRecipients: route({
    method: 'GET', path: '/workspaces/:workspaceId/campaigns/:campaignId/recipients', summary: 'Destinatarios y su estado', tag: 'campaigns',
    minRole: 'manager', params: Camp, query: C.CampaignRecipientsQuerySchema, response: C.CampaignRecipientPageSchema,
  }),
} as const;
